// @ts-nocheck
/**
 * WebSocket Service for Real-time Updates
 * Handles live dashboard updates and AI tutoring chat
 */

import { EventEmitter } from 'events';

export type WebSocketMessageType =
  | 'connection'
  | 'pong'
  | 'heartbeat_ack'
  | 'progress_update'
  | 'achievement'
  | 'insight'
  | 'activity'
  | 'tutoring_response'
  | 'permission_change'
  | 'error';

export interface WebSocketMessage {
  type: WebSocketMessageType;
  data?: any;
  timestamp: string;
}

export interface TutoringMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: {
    confidence?: number;
    suggestions?: string[];
    concepts?: string[];
  };
}

export interface PermissionChangeEvent {
  type: 'role_added' | 'role_removed' | 'permission_updated' | 'critical_revoked';
  userId: string;
  changes: {
    roles_added?: string[];
    roles_removed?: string[];
    permissions_added?: string[];
    permissions_removed?: string[];
    critical?: boolean;
    affected_resources?: string[];
  };
  timestamp: string;
  message?: string;
}

class WebSocketService extends EventEmitter {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000; // Maximum 30 second delay
  private pingInterval: NodeJS.Timeout | null = null;
  private isConnected = false;
  private circuitBreakerOpenUntil: number = 0;
  private consecutiveFailures = 0;
  private maxConsecutiveFailures = 3;
  private componentListeners = new Map<string, Set<string>>(); // Track listeners by component

  /**
   * Connect to family dashboard WebSocket
   */
  connectToDashboard(familyId: string, token: string): void {
    const wsUrl = import.meta.env.VITE_WS_URL || 'wss://staging.sigma08.com';
    const url = `${wsUrl}/v1/ws/family/${familyId}?token=${token}`;

    this.connect(url);
  }

  /**
   * Connect to AI tutoring WebSocket
   */
  connectToTutoringSession(sessionId: string, token: string): void {
    const wsUrl = import.meta.env.VITE_WS_URL || 'wss://staging.sigma08.com';
    const url = `${wsUrl}/v1/tutoring/ws/${sessionId}?token=${token}`;

    this.connect(url);
  }

  /**
   * Connect to permission updates WebSocket
   */
  connectToPermissionUpdates(token: string): void {
    const wsUrl = import.meta.env.VITE_WS_URL || 'wss://staging.sigma08.com';
    const url = `${wsUrl}/api/v1/ws/permissions?token=${token}`;

    this.connect(url);
  }

  private connect(url: string): void {
    try {
      // Close existing connection if any
      if (this.ws) {
        this.disconnect();
      }

      // Clean up stale event listeners during reconnection to prevent accumulation
      this.pruneStaleListeners();

      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.consecutiveFailures = 0; // Reset circuit breaker state on successful connection
        this.circuitBreakerOpenUntil = 0;
        this.emit('connected');

        // Start ping interval to keep connection alive
        this.startPingInterval();
      };

      this.ws.onmessage = event => {
        try {
          console.log('[WebSocketService] ========== RAW MESSAGE RECEIVED ==========');
          console.log('[WebSocketService] Raw data:', event.data);
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('[WebSocketService] Parsed message:', JSON.stringify(message, null, 2));
          console.log('[WebSocketService] Message type:', message.type);
          console.log('[WebSocketService] ===============================================');
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = error => {
        console.error('WebSocket error:', error);
        this.emit('error', error);
      };

      this.ws.onclose = event => {
        this.isConnected = false;
        this.stopPingInterval();
        this.emit('disconnected');

        // Attempt reconnection if not intentional close
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect(url);
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.emit('error', error);
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'connection':
        this.emit('connection_established', message.data);
        break;

      case 'pong':
      case 'heartbeat_ack':
        // Heartbeat response received
        break;

      case 'progress_update':
        this.emit('progress_update', message.data);
        break;

      case 'achievement':
        this.emit('achievement_unlocked', message.data);
        break;

      case 'insight':
        this.emit('new_insight', message.data);
        break;

      case 'activity':
        this.emit('activity_update', message.data);
        break;

      case 'tutoring_response':
        this.emit('tutoring_response', message.data);
        break;

      case 'permission_change':
        this.emit('permission_change', message.data);
        break;

      case 'error':
        this.emit('error', message.data);
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  /**
   * Send message through WebSocket
   */
  send(type: string, data?: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return;
    }

    const message = {
      type,
      data,
      timestamp: new Date().toISOString(),
    };

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Send AI tutoring message
   */
  sendTutoringMessage(content: string): void {
    this.send('student_message', {
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Subscribe to specific student updates
   */
  subscribeToStudent(studentId: string): void {
    this.send('subscribe', { studentId });
  }

  /**
   * Request data refresh
   */
  requestRefresh(dataType: string): void {
    this.send('request_refresh', { dataType });
  }

  /**
   * Acknowledge update receipt
   */
  acknowledgeUpdate(updateId: string): void {
    this.send('acknowledge', { updateId });
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send('heartbeat');
      }
    }, 30000); // Heartbeat every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private attemptReconnect(url: string): void {
    // Check if circuit breaker is open
    if (Date.now() < this.circuitBreakerOpenUntil) {
      // Circuit breaker is open, skipping reconnection attempt
      return;
    }

    this.reconnectAttempts++;
    this.consecutiveFailures++;

    // Open circuit breaker if too many consecutive failures
    if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
      this.circuitBreakerOpenUntil = Date.now() + 60000 * this.consecutiveFailures; // Increase backoff time
      // Circuit breaker opened due to consecutive failures - emitting event
      this.emit('circuit_breaker_opened', { failures: this.consecutiveFailures });
      return;
    }

    // Exponential backoff with maximum limit
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);

    // Attempting reconnection with exponential backoff

    setTimeout(() => {
      this.connect(url);
    }, delay);
  }

  /**
   * Manually disconnect WebSocket
   */
  disconnect(): void {
    this.stopPingInterval();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.reconnectAttempts = 0;

    // Clean up orphaned listeners (but keep tracked component listeners)
    this.pruneStaleListeners();
  }

  /**
   * Register a component as listening to specific events
   * This helps track listeners for proper cleanup
   */
  registerComponent(componentId: string, eventTypes: string[]): void {
    if (!this.componentListeners.has(componentId)) {
      this.componentListeners.set(componentId, new Set());
    }

    const componentEvents = this.componentListeners.get(componentId)!;
    eventTypes.forEach(eventType => {
      componentEvents.add(eventType);
    });
  }

  /**
   * Safely clean up listeners for a specific component
   * Use this in component unmount to prevent memory leaks
   */
  cleanupListeners(componentId: string): void {
    const componentEvents = this.componentListeners.get(componentId);
    if (componentEvents) {
      componentEvents.forEach(eventType => {
        // Remove all listeners for this event type (this is safe since it's a singleton service)
        this.removeAllListeners(eventType);
      });

      // Remove component tracking
      this.componentListeners.delete(componentId);
    }
  }

  /**
   * Prune stale listeners to prevent memory leaks during reconnections
   */
  private pruneStaleListeners(): void {
    // Get list of events that currently have listeners
    const eventNames = this.eventNames();

    // Check if we have too many listeners (indication of potential leak)
    eventNames.forEach(eventName => {
      const listenerCount = this.listenerCount(eventName as string);
      if (listenerCount > 10) {
        // Arbitrary threshold for too many listeners
        console.warn(
          `WebSocket service has ${listenerCount} listeners for event '${eventName}' - potential memory leak`
        );
        // Remove excess listeners but keep at least 2 for essential functionality
        const listeners = this.listeners(eventName as string);
        const excessListeners = listeners.slice(2);
        excessListeners.forEach(listener => {
          this.removeListener(eventName as string, listener);
        });
      }
    });
  }

  /**
   * Complete cleanup - removes all listeners and component tracking
   * Use this only when completely shutting down the service
   */
  cleanup(): void {
    this.disconnect();
    this.removeAllListeners();
    this.componentListeners.clear();
  }

  /**
   * Check if currently connected
   */
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  /**
   * Get memory usage statistics for debugging
   */
  getMemoryStats(): { totalListeners: number; eventTypes: string[]; components: string[] } {
    const eventNames = this.eventNames();
    const totalListeners = eventNames.reduce((total, eventName) => {
      return total + this.listenerCount(eventName as string);
    }, 0);

    return {
      totalListeners,
      eventTypes: eventNames.map(name => name.toString()),
      components: Array.from(this.componentListeners.keys()),
    };
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
